import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { 
  CreditCard,
  Eye,
  EyeSlash,
  SpinnerGap,
  CheckCircle,
  Warning,
  Trash,
  ArrowsClockwise,
  GoogleLogo,
  AppleLogo,
  FacebookLogo,
  ToggleLeft,
  ToggleRight,
  CloudArrowUp,
  Plugs,
  Files,
  Crown,
  Lock,
  Gear,
  Envelope
} from '@phosphor-icons/react';
import { cn } from '../../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminSettingsPage = () => {
  const [config, setConfig] = useState(null);
  const [oauthConfig, setOauthConfig] = useState(null);
  const [r2Config, setR2Config] = useState(null);
  const [tierConfig, setTierConfig] = useState(null);
  const [emailConfig, setEmailConfig] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [txnTotal, setTxnTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [secretKey, setSecretKey] = useState('');
  const [publishableKey, setPublishableKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configRes, txnRes, oauthRes, r2Res, tierRes, emailRes] = await Promise.all([
        axios.get(`${API}/admin/stripe-config`),
        axios.get(`${API}/admin/payments?limit=10`),
        axios.get(`${API}/admin/oauth-config`),
        axios.get(`${API}/admin/r2-config`),
        axios.get(`${API}/admin/tier-config`),
        axios.get(`${API}/admin/email-config`)
      ]);
      setConfig(configRes.data);
      setTransactions(txnRes.data.transactions);
      setTxnTotal(txnRes.data.total);
      setOauthConfig(oauthRes.data);
      setR2Config(r2Res.data);
      setTierConfig(tierRes.data);
      setEmailConfig(emailRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!secretKey.trim()) return;
    setSaving(true);
    setMessage(null);
    try {
      await axios.put(`${API}/admin/stripe-config`, {
        secret_key: secretKey,
        publishable_key: publishableKey
      }, { withCredentials: true });
      setMessage({ type: 'success', text: 'Stripe keys updated successfully.' });
      setSecretKey('');
      setPublishableKey('');
      await fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update Stripe keys.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/stripe-config`, { withCredentials: true });
      setMessage({ type: 'success', text: 'Database config removed. Using environment variable.' });
      await fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to remove config.' });
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <SpinnerGap className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-4xl" data-testid="admin-settings-page">
      <div className="mb-8">
        <h1 className="font-heading font-black text-2xl tracking-tighter mb-1">Settings</h1>
        <p className="text-sm text-muted-foreground">Manage payment, storage, and authentication settings</p>
      </div>

      {/* Status Banner */}
      {message && (
        <div className={cn(
          "p-3 rounded-sm border mb-6 flex items-center gap-2 text-sm",
          message.type === 'success' ? "bg-success/10 border-success/30 text-success" : "bg-destructive/10 border-destructive/30 text-destructive"
        )}>
          {message.type === 'success' ? <CheckCircle weight="fill" className="w-4 h-4" /> : <Warning weight="fill" className="w-4 h-4" />}
          {message.text}
        </div>
      )}

      {/* Current Config */}
      <div className="bg-card border border-border rounded-sm p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <CreditCard weight="fill" className="w-5 h-5 text-primary" />
          <h2 className="font-heading font-bold text-lg">Stripe Configuration</h2>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-muted/50 rounded-sm p-3">
            <p className="text-xs text-muted-foreground mb-1">Status</p>
            <p className={cn("font-bold text-sm", config?.configured ? "text-success" : "text-destructive")}>
              {config?.configured ? 'Configured' : 'Not Configured'}
            </p>
          </div>
          <div className="bg-muted/50 rounded-sm p-3">
            <p className="text-xs text-muted-foreground mb-1">Source</p>
            <p className="font-bold text-sm capitalize">{config?.source || 'none'}</p>
          </div>
        </div>

        {config?.configured && (
          <div className="bg-muted/50 rounded-sm p-3 mb-6">
            <p className="text-xs text-muted-foreground mb-1">Active Key</p>
            <p className="font-mono text-sm">{config?.key_preview}</p>
          </div>
        )}

        {/* Update Keys */}
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground block mb-1">Secret Key</label>
            <div className="relative">
              <input
                type={showKey ? 'text' : 'password'}
                value={secretKey}
                onChange={e => setSecretKey(e.target.value)}
                placeholder="sk_live_... or sk_test_..."
                className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm font-mono pr-10"
                data-testid="stripe-secret-key-input"
              />
              <button
                onClick={() => setShowKey(!showKey)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showKey ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground block mb-1">Publishable Key (optional)</label>
            <input
              type="text"
              value={publishableKey}
              onChange={e => setPublishableKey(e.target.value)}
              placeholder="pk_live_... or pk_test_..."
              className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm font-mono"
              data-testid="stripe-publishable-key-input"
            />
          </div>
          <div className="flex gap-3">
            <Button
              onClick={handleSave}
              disabled={!secretKey.trim() || saving}
              className="rounded-sm"
              data-testid="save-stripe-keys"
            >
              {saving ? <SpinnerGap className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Save Keys
            </Button>
            {config?.source === 'database' && (
              <Button
                variant="outline"
                onClick={handleDelete}
                className="rounded-sm border-destructive/50 text-destructive hover:bg-destructive hover:text-destructive-foreground"
                data-testid="remove-stripe-config"
              >
                <Trash className="w-4 h-4 mr-2" />
                Remove & Use Env
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* OAuth Providers */}
      <OAuthConfigSection config={oauthConfig} onRefresh={fetchData} />

      {/* R2 Storage */}
      <R2ConfigSection config={r2Config} onRefresh={fetchData} />

      {/* Tier Configuration */}
      <TierConfigSection config={tierConfig} onRefresh={fetchData} />

      {/* Email Configuration */}
      <EmailConfigSection config={emailConfig} onRefresh={fetchData} />

      {/* Payment History */}
      <div className="bg-card border border-border rounded-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <CreditCard weight="fill" className="w-5 h-5 text-muted-foreground" />
            <h2 className="font-heading font-bold text-lg">Payment History</h2>
          </div>
          <button onClick={fetchData} className="text-muted-foreground hover:text-foreground">
            <ArrowsClockwise className="w-5 h-5" />
          </button>
        </div>

        {transactions.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No transactions yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="payment-history-table">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground uppercase tracking-wider">
                  <th className="py-2 pr-4">Date</th>
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Amount</th>
                  <th className="py-2 pr-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((txn, i) => (
                  <tr key={i} className="border-b border-border/50">
                    <td className="py-2 pr-4 text-muted-foreground">
                      {new Date(txn.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-2 pr-4">{txn.email}</td>
                    <td className="py-2 pr-4 font-mono">
                      {txn.currency?.toUpperCase()} {txn.amount?.toFixed(2)}
                    </td>
                    <td className="py-2 pr-4">
                      <span className={cn(
                        "text-xs font-bold uppercase px-2 py-0.5 rounded-sm",
                        txn.payment_status === 'paid' && "bg-success/20 text-success",
                        txn.payment_status === 'pending' && "bg-amber-500/20 text-amber-500",
                        !['paid', 'pending'].includes(txn.payment_status) && "bg-muted text-muted-foreground"
                      )}>
                        {txn.payment_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {txnTotal > 10 && (
              <p className="text-xs text-muted-foreground mt-2">Showing 10 of {txnTotal} transactions</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// R2 Storage Configuration Section
const R2ConfigSection = ({ config, onRefresh }) => {
  const [form, setForm] = useState({
    account_id: '', access_key_id: '', secret_access_key: '',
    bucket_name: '', endpoint_url: '', public_url: ''
  });
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState(null);

  const handleSave = async () => {
    if (!form.access_key_id.trim() || !form.secret_access_key.trim()) return;
    setSaving(true);
    setMsg(null);
    try {
      await axios.put(`${API}/admin/r2-config`, form);
      setMsg({ type: 'success', text: 'R2 storage configuration updated.' });
      setForm({ account_id: '', access_key_id: '', secret_access_key: '', bucket_name: '', endpoint_url: '', public_url: '' });
      onRefresh();
    } catch (err) {
      setMsg({ type: 'error', text: 'Failed to update R2 config.' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setMsg(null);
    try {
      await axios.post(`${API}/admin/r2-config/test`);
      setMsg({ type: 'success', text: 'Connection successful — bucket is accessible.' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.detail || 'Connection test failed.' });
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/r2-config`);
      setMsg({ type: 'success', text: 'Database config removed. Using environment variables.' });
      onRefresh();
    } catch (err) {
      setMsg({ type: 'error', text: 'Failed to remove config.' });
    }
  };

  return (
    <div className="bg-card border border-border rounded-sm p-6 mb-6" data-testid="r2-config-section">
      <div className="flex items-center gap-3 mb-4">
        <CloudArrowUp weight="fill" className="w-5 h-5 text-primary" />
        <h2 className="font-heading font-bold text-lg">Storage (Cloudflare R2)</h2>
      </div>

      {msg && (
        <div className={cn(
          "p-3 rounded-sm border mb-4 flex items-center gap-2 text-sm",
          msg.type === 'success' ? "bg-success/10 border-success/30 text-success" : "bg-destructive/10 border-destructive/30 text-destructive"
        )}>
          {msg.type === 'success' ? <CheckCircle weight="fill" className="w-4 h-4" /> : <Warning weight="fill" className="w-4 h-4" />}
          {msg.text}
        </div>
      )}

      {/* Status */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-muted/50 rounded-sm p-3">
          <p className="text-xs text-muted-foreground mb-1">Status</p>
          <p className={cn("font-bold text-sm", config?.configured ? "text-success" : "text-destructive")}>
            {config?.configured ? 'Configured' : 'Not Configured'}
          </p>
        </div>
        <div className="bg-muted/50 rounded-sm p-3">
          <p className="text-xs text-muted-foreground mb-1">Source</p>
          <p className="font-bold text-sm capitalize">{config?.source || 'none'}</p>
        </div>
      </div>

      {config?.configured && (
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-muted/50 rounded-sm p-3">
            <p className="text-xs text-muted-foreground mb-1">Bucket</p>
            <p className="font-mono text-sm">{config.bucket_name}</p>
          </div>
          <div className="bg-muted/50 rounded-sm p-3">
            <p className="text-xs text-muted-foreground mb-1">Access Key</p>
            <p className="font-mono text-sm">{config.access_key_preview}</p>
          </div>
        </div>
      )}

      {/* Update Form */}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground block mb-1">Account ID</label>
            <input type="text" value={form.account_id} onChange={e => setForm(p => ({...p, account_id: e.target.value}))}
              placeholder={config?.account_id || "Cloudflare Account ID"}
              className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm font-mono" data-testid="r2-account-id" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground block mb-1">Bucket Name</label>
            <input type="text" value={form.bucket_name} onChange={e => setForm(p => ({...p, bucket_name: e.target.value}))}
              placeholder={config?.bucket_name || "testready-assets"}
              className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm font-mono" data-testid="r2-bucket-name" />
          </div>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground block mb-1">Access Key ID</label>
          <input type="text" value={form.access_key_id} onChange={e => setForm(p => ({...p, access_key_id: e.target.value}))}
            placeholder="R2 Access Key ID"
            className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm font-mono" data-testid="r2-access-key-id" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground block mb-1">Secret Access Key</label>
          <div className="relative">
            <input type={showSecret ? 'text' : 'password'} value={form.secret_access_key} onChange={e => setForm(p => ({...p, secret_access_key: e.target.value}))}
              placeholder="R2 Secret Access Key"
              className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm font-mono pr-10" data-testid="r2-secret-key" />
            <button onClick={() => setShowSecret(!showSecret)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              {showSecret ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground block mb-1">Endpoint URL</label>
          <input type="text" value={form.endpoint_url} onChange={e => setForm(p => ({...p, endpoint_url: e.target.value}))}
            placeholder={config?.endpoint_url || "https://<account-id>.r2.cloudflarestorage.com"}
            className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm font-mono" data-testid="r2-endpoint-url" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground block mb-1">Public URL (Optional — custom domain or r2.dev)</label>
          <input type="text" value={form.public_url} onChange={e => setForm(p => ({...p, public_url: e.target.value}))}
            placeholder="https://assets.testready.co.uk"
            className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm font-mono" data-testid="r2-public-url" />
        </div>

        <div className="flex gap-3 pt-1">
          <Button onClick={handleSave} disabled={!form.access_key_id.trim() || !form.secret_access_key.trim() || saving} className="rounded-sm" data-testid="save-r2-config">
            {saving ? <SpinnerGap className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
            Save Keys
          </Button>
          {config?.configured && (
            <Button variant="outline" onClick={handleTest} disabled={testing} className="rounded-sm" data-testid="test-r2-connection">
              {testing ? <SpinnerGap className="w-4 h-4 animate-spin mr-2" /> : <Plugs className="w-4 h-4 mr-2" />}
              Test Connection
            </Button>
          )}
          {config?.source === 'database' && (
            <Button variant="outline" onClick={handleDelete}
              className="rounded-sm border-destructive/50 text-destructive hover:bg-destructive hover:text-destructive-foreground" data-testid="remove-r2-config">
              <Trash className="w-4 h-4 mr-2" />
              Remove & Use Env
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// OAuth Configuration Section
const OAuthConfigSection = ({ config, onRefresh }) => {
  const [appleForm, setAppleForm] = useState({ client_id: '', client_secret: '', team_id: '', key_id: '' });
  const [fbForm, setFbForm] = useState({ app_id: '', app_secret: '' });
  const [saving, setSaving] = useState(null);
  const [msg, setMsg] = useState(null);

  const handleSaveProvider = async (provider) => {
    setSaving(provider);
    setMsg(null);
    try {
      const body = { provider, enabled: true };
      if (provider === 'apple') {
        body.client_id = appleForm.client_id;
        body.client_secret = appleForm.client_secret;
        body.team_id = appleForm.team_id;
        body.key_id = appleForm.key_id;
      } else {
        body.client_id = fbForm.app_id;
        body.client_secret = fbForm.app_secret;
      }
      await axios.put(`${API}/admin/oauth-config`, body, { withCredentials: true });
      setMsg({ type: 'success', text: `${provider === 'apple' ? 'Apple' : 'Facebook'} OAuth saved and enabled.` });
      if (provider === 'apple') setAppleForm({ client_id: '', client_secret: '', team_id: '', key_id: '' });
      else setFbForm({ app_id: '', app_secret: '' });
      onRefresh();
    } catch (err) {
      setMsg({ type: 'error', text: `Failed to save ${provider} config.` });
    } finally {
      setSaving(null);
    }
  };

  const handleDisable = async (provider) => {
    try {
      await axios.put(`${API}/admin/oauth-config`, { provider, enabled: false }, { withCredentials: true });
      onRefresh();
    } catch (err) {
      console.error(err);
    }
  };

  if (!config) return null;

  return (
    <div className="bg-card border border-border rounded-sm p-6 mb-6" data-testid="oauth-config-section">
      <div className="flex items-center gap-3 mb-4">
        <GoogleLogo weight="fill" className="w-5 h-5 text-primary" />
        <h2 className="font-heading font-bold text-lg">Social Login Providers</h2>
      </div>

      {msg && (
        <div className={cn(
          "p-3 rounded-sm border mb-4 flex items-center gap-2 text-sm",
          msg.type === 'success' ? "bg-success/10 border-success/30 text-success" : "bg-destructive/10 border-destructive/30 text-destructive"
        )}>
          {msg.type === 'success' ? <CheckCircle weight="fill" className="w-4 h-4" /> : <Warning weight="fill" className="w-4 h-4" />}
          {msg.text}
        </div>
      )}

      {/* Google — Always On */}
      <div className="flex items-center justify-between p-3 bg-muted/50 rounded-sm mb-3">
        <div className="flex items-center gap-3">
          <GoogleLogo weight="fill" className="w-5 h-5" />
          <div>
            <p className="font-bold text-sm">Google</p>
            <p className="text-xs text-muted-foreground">Managed by Emergent — always available</p>
          </div>
        </div>
        <span className="text-xs font-bold text-success uppercase px-2 py-0.5 bg-success/20 rounded-sm">Active</span>
      </div>

      {/* Apple */}
      <div className="border border-border rounded-sm p-4 mb-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <AppleLogo weight="fill" className="w-5 h-5" />
            <div>
              <p className="font-bold text-sm">Apple</p>
              <p className="text-xs text-muted-foreground">
                {config.apple?.configured ? `Configured (${config.apple.client_id_preview})` : 'Not configured'}
              </p>
            </div>
          </div>
          {config.apple?.enabled ? (
            <button onClick={() => handleDisable('apple')} className="text-success" data-testid="toggle-apple">
              <ToggleRight weight="fill" className="w-8 h-8" />
            </button>
          ) : (
            <span className="text-xs text-muted-foreground px-2 py-0.5 bg-muted rounded-sm">Disabled</span>
          )}
        </div>
        {!config.apple?.configured && (
          <div className="space-y-2">
            <input type="text" placeholder="Client ID (Service ID)" value={appleForm.client_id} onChange={e => setAppleForm(p => ({...p, client_id: e.target.value}))}
              className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm font-mono" data-testid="apple-client-id" />
            <input type="password" placeholder="Client Secret (Key)" value={appleForm.client_secret} onChange={e => setAppleForm(p => ({...p, client_secret: e.target.value}))}
              className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm font-mono" data-testid="apple-client-secret" />
            <div className="grid grid-cols-2 gap-2">
              <input type="text" placeholder="Team ID" value={appleForm.team_id} onChange={e => setAppleForm(p => ({...p, team_id: e.target.value}))}
                className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm font-mono" />
              <input type="text" placeholder="Key ID" value={appleForm.key_id} onChange={e => setAppleForm(p => ({...p, key_id: e.target.value}))}
                className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm font-mono" />
            </div>
            <Button size="sm" disabled={!appleForm.client_id || saving === 'apple'} onClick={() => handleSaveProvider('apple')} className="rounded-sm" data-testid="save-apple">
              {saving === 'apple' ? <SpinnerGap className="w-4 h-4 animate-spin mr-2" /> : null}
              Save & Enable Apple
            </Button>
          </div>
        )}
      </div>

      {/* Facebook */}
      <div className="border border-border rounded-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <FacebookLogo weight="fill" className="w-5 h-5" />
            <div>
              <p className="font-bold text-sm">Facebook</p>
              <p className="text-xs text-muted-foreground">
                {config.facebook?.configured ? `Configured (${config.facebook.app_id_preview})` : 'Not configured'}
              </p>
            </div>
          </div>
          {config.facebook?.enabled ? (
            <button onClick={() => handleDisable('facebook')} className="text-success" data-testid="toggle-facebook">
              <ToggleRight weight="fill" className="w-8 h-8" />
            </button>
          ) : (
            <span className="text-xs text-muted-foreground px-2 py-0.5 bg-muted rounded-sm">Disabled</span>
          )}
        </div>
        {!config.facebook?.configured && (
          <div className="space-y-2">
            <input type="text" placeholder="App ID" value={fbForm.app_id} onChange={e => setFbForm(p => ({...p, app_id: e.target.value}))}
              className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm font-mono" data-testid="fb-app-id" />
            <input type="password" placeholder="App Secret" value={fbForm.app_secret} onChange={e => setFbForm(p => ({...p, app_secret: e.target.value}))}
              className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm font-mono" data-testid="fb-app-secret" />
            <Button size="sm" disabled={!fbForm.app_id || saving === 'facebook'} onClick={() => handleSaveProvider('facebook')} className="rounded-sm" data-testid="save-facebook">
              {saving === 'facebook' ? <SpinnerGap className="w-4 h-4 animate-spin mr-2" /> : null}
              Save & Enable Facebook
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};


// Tier Configuration Section
const TierConfigSection = ({ config, onRefresh }) => {
  const [tiers, setTiers] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (config) {
      setTiers(JSON.parse(JSON.stringify(config.tiers || {})));
      setPricing(JSON.parse(JSON.stringify(config.pricing || {})));
      setAvailableCategories(config.available_categories || []);
    }
  }, [config]);

  const FEATURE_LABELS = {
    mock_exam: 'Mock Exams',
    smart_review: 'Smart Review (Spaced Repetition)',
    bookmarks: 'Question Bookmarks',
    hazard_perception: 'Hazard Perception Practice',
    ai_explanations: 'AI-Powered Explanations',
  };

  const TIER_NAMES = { free: 'Free', standard: 'Core', premium: 'Pass Faster' };
  const TIER_STYLES = {
    free: 'border-border',
    standard: 'border-blue-500/40',
    premium: 'border-accent/40',
  };

  const toggleFeature = (tierKey, feature) => {
    setTiers(prev => ({
      ...prev,
      [tierKey]: {
        ...prev[tierKey],
        [feature]: !prev[tierKey][feature],
      }
    }));
  };

  const toggleAllCategories = (tierKey, checked) => {
    setTiers(prev => ({
      ...prev,
      [tierKey]: {
        ...prev[tierKey],
        all_categories: checked,
        categories: checked ? availableCategories.map(c => c.id) : [],
      }
    }));
  };

  const toggleCategory = (tierKey, categoryId) => {
    setTiers(prev => {
      const currentCats = prev[tierKey]?.categories || [];
      const newCats = currentCats.includes(categoryId)
        ? currentCats.filter(c => c !== categoryId)
        : [...currentCats, categoryId];
      const allChecked = newCats.length === availableCategories.length;
      return {
        ...prev,
        [tierKey]: {
          ...prev[tierKey],
          categories: newCats,
          all_categories: allChecked,
        }
      };
    });
  };

  const updatePrice = (tierKey, value) => {
    setPricing(prev => ({
      ...prev,
      [tierKey]: parseFloat(value) || 0,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMsg(null);
    try {
      await axios.put(`${API}/admin/tier-config`, { tiers, pricing }, { withCredentials: true });
      setMsg({ type: 'success', text: 'Tier configuration saved successfully.' });
      onRefresh();
    } catch (err) {
      setMsg({ type: 'error', text: 'Failed to save tier configuration.' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    setMsg(null);
    try {
      const { data } = await axios.post(`${API}/admin/tier-config/reset`, {}, { withCredentials: true });
      setTiers(data.tiers);
      setPricing(data.pricing);
      setMsg({ type: 'success', text: 'Tier configuration reset to defaults.' });
      onRefresh();
    } catch (err) {
      setMsg({ type: 'error', text: 'Failed to reset tier configuration.' });
    } finally {
      setResetting(false);
    }
  };

  if (!tiers || !pricing) return null;

  return (
    <div className="bg-card border border-border rounded-sm p-6 mb-6" data-testid="tier-config-section">
      <div className="flex items-center gap-3 mb-4">
        <Crown weight="fill" className="w-5 h-5 text-accent" />
        <h2 className="font-heading font-bold text-lg">Subscription Tiers</h2>
      </div>

      {msg && (
        <div className={cn(
          "p-3 rounded-sm border mb-4 flex items-center gap-2 text-sm",
          msg.type === 'success' ? "bg-success/10 border-success/30 text-success" : "bg-destructive/10 border-destructive/30 text-destructive"
        )}>
          {msg.type === 'success' ? <CheckCircle weight="fill" className="w-4 h-4" /> : <Warning weight="fill" className="w-4 h-4" />}
          {msg.text}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {['free', 'standard', 'premium'].map(tierKey => {
          const tierCats = tiers[tierKey]?.categories || [];
          const isAllCats = tiers[tierKey]?.all_categories || false;
          return (
            <div key={tierKey} className={cn("border-2 rounded-sm p-4", TIER_STYLES[tierKey])} data-testid={`tier-config-${tierKey}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-bold">{TIER_NAMES[tierKey]}</h3>
                {tierKey !== 'free' && (
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-muted-foreground">£</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={pricing[tierKey]}
                      onChange={e => updatePrice(tierKey, e.target.value)}
                      className="w-16 bg-background border border-border rounded-sm px-2 py-1 text-sm font-mono text-right"
                      data-testid={`price-${tierKey}`}
                    />
                    <span className="text-xs text-muted-foreground">/mo</span>
                  </div>
                )}
                {tierKey === 'free' && (
                  <span className="text-xs text-muted-foreground font-mono">Free</span>
                )}
              </div>

              {/* Feature toggles */}
              <div className="space-y-2 mb-3">
                {Object.keys(FEATURE_LABELS).map(feature => (
                  <label
                    key={feature}
                    className="flex items-center gap-2 cursor-pointer group"
                    data-testid={`toggle-${tierKey}-${feature}`}
                  >
                    <input
                      type="checkbox"
                      checked={tiers[tierKey]?.[feature] || false}
                      onChange={() => toggleFeature(tierKey, feature)}
                      className="rounded border-border"
                    />
                    <span className="text-xs group-hover:text-foreground text-muted-foreground">
                      {FEATURE_LABELS[feature]}
                    </span>
                  </label>
                ))}
              </div>

              {/* Category selection with nested checkboxes */}
              <div className="border-t border-border pt-3">
                <label
                  className="flex items-center gap-2 cursor-pointer group mb-2"
                  data-testid={`toggle-${tierKey}-all_categories`}
                >
                  <input
                    type="checkbox"
                    checked={isAllCats}
                    onChange={e => toggleAllCategories(tierKey, e.target.checked)}
                    className="rounded border-border"
                  />
                  <span className="text-xs font-bold group-hover:text-foreground text-muted-foreground">
                    All Categories
                  </span>
                </label>
                <div className="ml-5 space-y-1.5 max-h-48 overflow-y-auto">
                  {availableCategories.map(cat => (
                    <label
                      key={cat.id}
                      className="flex items-center gap-2 cursor-pointer group"
                      data-testid={`toggle-${tierKey}-cat-${cat.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={isAllCats || tierCats.includes(cat.id)}
                        onChange={() => toggleCategory(tierKey, cat.id)}
                        disabled={isAllCats}
                        className="rounded border-border"
                      />
                      <span className={cn(
                        "text-[11px] group-hover:text-foreground",
                        isAllCats ? "text-muted-foreground/50" : "text-muted-foreground"
                      )}>
                        {cat.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex gap-3">
        <Button onClick={handleSave} disabled={saving} className="rounded-sm" data-testid="save-tier-config">
          {saving ? <SpinnerGap className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
          Save Tier Config
        </Button>
        <Button variant="outline" onClick={handleReset} disabled={resetting} className="rounded-sm" data-testid="reset-tier-config">
          {resetting ? <SpinnerGap className="w-4 h-4 animate-spin mr-2" /> : <ArrowsClockwise className="w-4 h-4 mr-2" />}
          Reset to Defaults
        </Button>
      </div>
    </div>
  );
};


// Email Configuration Section (Resend)
const EmailConfigSection = ({ config, onRefresh }) => {
  const [apiKey, setApiKey] = useState('');
  const [senderEmail, setSenderEmail] = useState('onboarding@resend.dev');
  const [enabled, setEnabled] = useState(true);
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingDigest, setTestingDigest] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (config) {
      setSenderEmail(config.sender_email || 'onboarding@resend.dev');
      setEnabled(config.enabled !== false);
    }
  }, [config]);

  const handleSave = async () => {
    if (!apiKey) {
      setMsg({ type: 'error', text: 'API key is required' });
      return;
    }
    setSaving(true);
    setMsg(null);
    try {
      await axios.put(`${API}/admin/email-config`, {
        api_key: apiKey,
        sender_email: senderEmail,
        enabled,
      }, { withCredentials: true });
      setMsg({ type: 'success', text: 'Email configuration saved.' });
      setApiKey('');
      onRefresh();
    } catch (err) {
      setMsg({ type: 'error', text: 'Failed to save email config.' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setMsg(null);
    try {
      const { data } = await axios.post(`${API}/admin/email-config/test`, {}, { withCredentials: true });
      setMsg({ type: 'success', text: data.message });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.detail || 'Test email failed.' });
    } finally {
      setTesting(false);
    }
  };

  const handleTestDigest = async () => {
    setTestingDigest(true);
    setMsg(null);
    try {
      const { data } = await axios.post(`${API}/admin/email-config/test-digest`, {}, { withCredentials: true });
      setMsg({ type: 'success', text: data.message });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.detail || 'Digest email failed.' });
    } finally {
      setTestingDigest(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/email-config`, { withCredentials: true });
      setMsg({ type: 'success', text: 'Email config removed.' });
      onRefresh();
    } catch (err) {
      setMsg({ type: 'error', text: 'Failed to remove email config.' });
    }
  };

  return (
    <div className="bg-card border border-border rounded-sm p-6 mb-6" data-testid="email-config-section">
      <div className="flex items-center gap-3 mb-4">
        <Envelope weight="fill" className="w-5 h-5 text-primary" />
        <h2 className="font-heading font-bold text-lg">Email Reminders (Resend)</h2>
      </div>

      <p className="text-sm text-muted-foreground mb-4">
        Send daily study reminders via email to users without push notifications. Free tier: 100 emails/day.
      </p>

      {msg && (
        <div className={cn(
          "p-3 rounded-sm border mb-4 flex items-center gap-2 text-sm",
          msg.type === 'success' ? "bg-success/10 border-success/30 text-success" : "bg-destructive/10 border-destructive/30 text-destructive"
        )}>
          {msg.type === 'success' ? <CheckCircle weight="fill" className="w-4 h-4" /> : <Warning weight="fill" className="w-4 h-4" />}
          {msg.text}
        </div>
      )}

      <div className="space-y-3 mb-4">
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground block mb-1">Status</label>
          <p className="text-sm">
            {config?.configured ? (
              <span className="text-success font-medium flex items-center gap-1"><CheckCircle weight="fill" className="w-4 h-4" /> Configured ({config.source})</span>
            ) : (
              <span className="text-muted-foreground">Not configured</span>
            )}
          </p>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground block mb-1">Resend API Key</label>
          <div className="flex gap-2">
            <input
              type={showKey ? "text" : "password"}
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              placeholder={config?.configured ? `Current: ${config.key_preview}` : "re_xxxxxxxxxx"}
              className="flex-1 bg-background border border-border rounded-sm px-3 py-2 text-sm font-mono"
              data-testid="resend-api-key"
            />
            <Button variant="outline" size="sm" onClick={() => setShowKey(!showKey)} className="rounded-sm px-3">
              {showKey ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground block mb-1">Sender Email</label>
          <input
            type="email"
            value={senderEmail}
            onChange={e => setSenderEmail(e.target.value)}
            className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm"
            data-testid="sender-email"
          />
          <p className="text-[10px] text-muted-foreground mt-1">Use onboarding@resend.dev for testing, or your verified domain for production.</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} className="rounded" />
          <label className="text-sm">Enable email reminders</label>
        </div>
      </div>

      <div className="flex gap-3">
        <Button onClick={handleSave} disabled={saving} className="rounded-sm" data-testid="save-email-config">
          {saving ? <SpinnerGap className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
          Save
        </Button>
        {config?.configured && (
          <>
            <Button variant="outline" onClick={handleTest} disabled={testing} className="rounded-sm" data-testid="test-email">
              {testing ? <SpinnerGap className="w-4 h-4 animate-spin mr-2" /> : <Envelope className="w-4 h-4 mr-2" />}
              Test Reminder
            </Button>
            <Button variant="outline" onClick={handleTestDigest} disabled={testingDigest} className="rounded-sm" data-testid="test-digest-email">
              {testingDigest ? <SpinnerGap className="w-4 h-4 animate-spin mr-2" /> : <Envelope className="w-4 h-4 mr-2" />}
              Test Digest
            </Button>
            <Button variant="outline" onClick={handleDelete} className="rounded-sm text-destructive border-destructive/30">
              <Trash className="w-4 h-4 mr-2" />
              Remove
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
